<template>
  <div v-if="isShown" class="sf-banner" :class="outerContainer">
    <div class="sf-banner-left">
      <span class="sf-banner-icon icon" :class="effectiveIconClass" />
      <span class="sf-banner-text">
        <span v-if="title" class="sf-banner-title">{{ title }}</span>
        <span class="sf-banner-description"
          ><slot><span v-dompurify-html="descriptionHtml"></span></slot
        ></span>
        <span v-if="hasLink" class="sf-banner-link"
          ><slot name="link"><span v-dompurify-html="linkHtml"></span></slot
        ></span>
      </span>
    </div>
    <div class="sf-banner-right">
      <div v-if="hasActions" class="sf-banner-actions">
        <slot name="actions">
          <span v-dompurify-html="actionsHtml"></span>
        </slot>
      </div>
      <span
        v-if="showClose"
        class="sf-banner-close icon icon-close"
        data-testid="sf-banner-close"
        @click="close"
      />
    </div>
  </div>
</template>
<script setup lang="ts">
import { bannerVariants, BannerVariant } from '@app/shared/types/banner'

import { icons, levels, BannerIconType, bannerLevelType } from './constants'
import { computed, ref, useSlots } from 'vue'

// eslint-disable-next-line no-undef
defineOptions({
  name: 'SfBannerBase',
})

const slots = useSlots()

interface Props {
  hasLongText?: boolean
  iconClass?: BannerIconType
  isFullWidth?: boolean
  level: bannerLevelType
  selfManaged?: boolean
  showClose?: boolean
  title?: string
  variant: BannerVariant
  descriptionHtml?: string
  actionsHtml?: string
  linkHtml?: string
}

const props = withDefaults(defineProps<Props>(), {
  hasLongText: false,
  iconClass: '',
  isFullWidth: false,
  level: levels.system,
  title: '',
  variant: bannerVariants.info,
  descriptionHtml: '',
  actionsHtml: '',
  linkHtml: '',
})

const isShown = ref(true)

const effectiveIconClass = computed(() => {
  if (props.iconClass) {
    return props.iconClass
  }

  return icons[props.variant]
})

const hasActions = computed(() => {
  return !!slots.actions || !!props.actionsHtml
})

const hasLink = computed(() => {
  return !!slots.link || !!props.linkHtml
})

const outerContainer = computed(() => {
  const effectiveClass = `sf-banner-${props.level}-${props.variant}`
  return {
    [effectiveClass]: true,
    'sf-banner-long-text': props.hasLongText,
    [`${effectiveClass}--full-width`]: props.isFullWidth,
  }
})

const emit = defineEmits(['close'])

const close = (): void => {
  if (props.selfManaged) {
    isShown.value = false
  }

  emit('close')
}
</script>
